import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download, ShieldCheck, Globe2, Zap, Truck, Sparkles } from "lucide-react";
import { SITE, HERO_IMAGE, FACTORY_IMAGE, WAREHOUSE_IMAGE, CONTROL_IMAGE } from "@/lib/site";
import { CATEGORIES } from "@/data/categories";
import CategoryCard from "@/components/CategoryCard";
import BrandsMarquee from "@/components/BrandsMarquee";

function Stat({ value, suffix, label }) {
  return (
    <div>
      <div className="font-display text-4xl md:text-5xl font-extrabold text-slate-900">
        {value}<span className="gfe-gradient-text">{suffix}</span>
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">{label}</div>
    </div>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gfe-dot-grid opacity-60" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 gfe-gradient-bg" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 lg:px-8 pt-16 md:pt-24 pb-16 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white/70 backdrop-blur text-xs text-slate-600">
              <Sparkles className="w-3.5 h-3.5 text-orange-500" /> One-Stop Sourcing Partner for Industry
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tighter leading-[1.02] text-slate-900">
              Your <span className="gfe-gradient-text">global partner</span><br />
              in industrial solutions.
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              From electricals and automation to lubricants, safety and packaging — we deliver <b>17+ categories</b> of certified industrial supply, sourced from <b>80+ trusted global brands</b>.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" data-testid="hero-quote-btn" className="btn-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={SITE.catalogue} target="_blank" rel="noreferrer" data-testid="hero-catalogue-btn"
                className="btn-ghost inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold">
                <Download className="w-4 h-4" /> Download Catalogue
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6">
              <Stat value={SITE.stats.categories} suffix="+" label="Categories" />
              <Stat value={SITE.stats.brandPartners} suffix="+" label="Brand Partners" />
              <Stat value={SITE.stats.industries} suffix="+" label="Industries" />
              <Stat value={SITE.stats.countries} suffix="+" label="Countries" />
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl">
              <img src={HERO_IMAGE} alt="Industrial automation" className="w-full h-[420px] md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 via-transparent to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between">
                <div className="text-white">
                  <div className="text-[11px] uppercase tracking-widest text-white/70">Featured</div>
                  <div className="font-display text-2xl font-bold leading-tight max-w-[220px] mt-1">Industrial Automation & Robotics</div>
                </div>
                <Link to="/products/industrial-automation" data-testid="hero-featured-link"
                  className="inline-flex items-center gap-1 text-white text-sm font-medium bg-white/15 backdrop-blur px-3 py-1.5 rounded-full border border-white/30">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
            <div className="absolute -left-6 -bottom-6 hidden md:flex items-center gap-3 bg-white border border-slate-200 rounded-2xl shadow-xl px-4 py-3">
              <img src={SITE.logo} alt="logo" className="h-9 w-auto" />
              <div className="leading-tight">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">100% Quality</div>
                <div className="text-sm font-semibold text-slate-900">Assured Sourcing</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-20 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 items-end mb-12">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Why Green Fuse</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2">
              Built for teams that can&apos;t afford <span className="gfe-gradient-text">downtime.</span>
            </h2>
          </div>
          <p className="text-slate-600 max-w-xl md:justify-self-end">
            Every part we ship is vetted for compliance, durability and fit. Our sourcing desk works around the clock to keep your plant running.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { Icon: Globe2, title: "Global Sourcing Network", body: "Direct relationships with 80+ manufacturers across Europe, Japan, US and India.", img: FACTORY_IMAGE },
            { Icon: ShieldCheck, title: "Quality You Can Trust", body: "Compliance-vetted, spec-matched parts with full material and batch traceability.", img: CONTROL_IMAGE },
            { Icon: Zap, title: "Responsive Partnership", body: "Dedicated sourcing manager, 24-hour quotes and urgent turnaround options.", img: HERO_IMAGE },
            { Icon: Truck, title: "Reliable Fulfilment", body: "Consistent inventory planning, kitting and multi-modal logistics.", img: WAREHOUSE_IMAGE },
          ].map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-xl transition">
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-0 group-hover:opacity-30 gfe-gradient-bg transition-opacity" />
              <v.Icon className="w-6 h-6 text-slate-900" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-bold mt-6">{v.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <BrandsMarquee />

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-20 md:py-24" id="products">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Explore</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2">
              17 categories. <span className="gfe-gradient-text">One partner.</span>
            </h2>
          </div>
          <Link to="/products" data-testid="home-view-all" className="btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium">
            View all products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {CATEGORIES.slice(0, 8).map((c, i) => <CategoryCard key={c.slug} cat={c} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 md:p-16">
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 gfe-gradient-bg" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Ready when you are</div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-3 leading-tight">
                Get a competitive quote in <span className="gfe-gradient-text">24 hours.</span>
              </h2>
              <p className="text-slate-300 mt-4 max-w-lg">
                Share your requirements — brand, part number, quantity — and our sourcing desk will get back with pricing, lead time and alternatives.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/quote" data-testid="cta-quote" className="btn-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={SITE.catalogue} target="_blank" rel="noreferrer" data-testid="cta-catalogue"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold border border-white/25 hover:border-white/60 transition">
                <Download className="w-4 h-4" /> Download Catalogue
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
